import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { KeyArrayMap } from "@merri/common";
import { Subject, filter, of, switchMap, takeUntil } from "rxjs";
import { VisionboardItem } from "../../../models";
import { VisionboardCategoriesService, VisionboardItemSearchService } from "../../../services";
import { VisionboardGlobalItemSearchService } from "../../services";
import { ForceVisionboardItemAddRemoveButton } from "../visionboard-item-add-remove-button/visionboard-item-add-remove-button.component";
import { VisionboardItemSearchResultsComponent } from "../visionboard-item-search-results/visionboard-item-search-results.component";

export interface GlobalSearchSelection {
  category?: [string, string];
  item?: VisionboardItem;
}

@Component({
  selector: "app-visionboard-global-search-result",
  templateUrl: "./visionboard-global-search-result.component.html",
  styleUrls: ["./visionboard-global-search-result.component.scss"],
  providers: [
    {
      provide: VisionboardItemSearchService,
      useExisting: VisionboardGlobalItemSearchService,
    },
  ],
  standalone: false,
})
export class VisionboardGlobalSearchResultComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  foundCategoryMap: KeyArrayMap<string>;
  foundItemsCount: number;
  searchTerm: string;
  loading: boolean = true;
  hasFoundCategories: boolean;

  @Input() itemCustomAddRemoveTemplate: TemplateRef<void>;
  @Input() source: string;
  @Input() forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton;

  @ViewChild(VisionboardItemSearchResultsComponent) itemSearchResults: VisionboardItemSearchResultsComponent;
  @Output() resultSelect: EventEmitter<GlobalSearchSelection> = new EventEmitter<GlobalSearchSelection>();

  constructor(
    private _visionboardGlobalItemSearchService: VisionboardGlobalItemSearchService,
    private _visionboardCategoriesService: VisionboardCategoriesService,
    private _visionboardItemSearchService: VisionboardItemSearchService,
  ) {}

  ngOnInit(): void {
    this._visionboardGlobalItemSearchService.searchTerm$
      .pipe(
        filter(searchTerm => !!searchTerm),
        switchMap(searchTerm => {
          this.searchTerm = searchTerm;
          this._searchSubcategories(this.searchTerm);
          this.loading = true;
          return of(false);
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this._visionboardItemSearchService.currentItems$
      .pipe(
        filter(searchItemResult => searchItemResult.count != null),
        takeUntil(this._destroyed$),
      )
      .subscribe({
        next: searchItemResult => {
          this.foundItemsCount = searchItemResult.count;
          this.loading = !(searchItemResult.initialLoad == null);
        },
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onSubcategoryClick(category: string, subcategory: string): void {
    this.resultSelect.emit({
      category: [category, subcategory],
    });
  }

  onItemClicked(item: VisionboardItem): void {
    this.resultSelect.emit({
      item: item,
    });
  }

  private _searchSubcategories(searchTerm: string): void {
    const categoriesMap = this._visionboardCategoriesService.getCategoriesMap();
    const foundCategoryMap: KeyArrayMap<string> = {};
    const applicableSubCategoryIds: number[] = this._visionboardGlobalItemSearchService.getSubCategoryIds();
    const searchTokens: string[] = searchTerm.split(" ").map(s => s.trim());

    Object.keys(categoriesMap).forEach(categoryMapKey => {
      const foundSubcategories = categoriesMap[categoryMapKey].filter(subcategory => {
        if (searchTokens.find(st => subcategory.toLocaleLowerCase().includes(st.toLocaleLowerCase()))) {
          const subCategoryData = this._visionboardCategoriesService.getCurrentSubcategories().find(sc => sc.name === subcategory);

          return applicableSubCategoryIds.includes(subCategoryData.subCategoryId);
        }
      });

      if (foundSubcategories.length) {
        foundCategoryMap[categoryMapKey] = foundSubcategories;
      }
    });

    this.foundCategoryMap = foundCategoryMap;
    this.hasFoundCategories = this.foundCategoryMap && Object.keys(this.foundCategoryMap).length > 0;
  }
}
